import React, { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import dillLogoWithText from '../../assets/svg/dill_logo_with_text.svg';
import { AppButton } from '../../components/general/AppButton/AppButton';
import { AppInputField } from '../../components/general/AppInputField/AppInputField';
import {
  getPaymentRequestDetailsForPayment,
  paymentRequestFiservPayment,
  paymentRequestPayment,
  paymentRequestDwollaPayment,
} from '../../redux/services/paymentRequestsService';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { twMerge } from 'tailwind-merge';
import type {
  Stripe,
  StripeCardElementChangeEvent,
  StripeElements,
  PaymentMethod,
} from '@stripe/stripe-js';
import { closeMessageModal, openMessageModal } from '../../redux/globalSlices/genericSlice';
import { useLocation } from 'react-router-dom';
import {
  appDelay,
  calculateFiservFee,
  calculateStripePartialFacilitationFeesAndAmount,
  calculateWillCallFeesAndAmount,
  capitalizeWordsInSentence,
  DillPaymentRequestResponse,
  FiservCardDetails,
  formatMoney,
} from '@dill/dill-shared';
import { getBasicSupplier } from '../../redux/services/buyersService';
import { Bank, CreditCard, File } from 'phosphor-react';
import { PayEmailConfirmationModal } from './PayEmailConfirmationModal/PayEmailConfirmationModal';
import JobSavingSuccess from '../RequestJobInformationSheet/JobSavingSuccess';
import { Download } from 'react-feather';
import { AppRadioButton } from '../../components/general/AppRadioButton/AppRadioButton';
import AppFiservCardInput, {
  FiservCardResult,
} from '../../components/general/AppFiservCardInput/AppFiservCardInput';
import CardBrands from '../../assets/images/card-brand.png';
import AppCustom3DSFiservCardInput from '../../components/general/AppCustom3DSFiservCardInput/AppCustom3DSFiservCardInput';
import { usePlaidLink } from 'react-plaid-link';
import type { PlaidLinkOnSuccess } from 'react-plaid-link';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { getPlaidLinkToken, getPlaidLinkTokenNoAuth } from '../../redux/services/authService';
import * as Sentry from '@sentry/react';
import { useIdempotencyKey } from '../../utils/hooks/useIdempotencyKey';
import { IDEMPOTENT_REPLAY_SUBTITLE, IDEMPOTENT_REPLAY_TITLE } from '../../utils/idempotency';

type PageState = 'MAKE_PAYMENT' | 'PAYMENT_FAILED' | 'PAYMENT_SUCCESS' | 'PAYMENT_FETCH_ERROR';
function PaymentRequestPaymentPage() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const stripe: Stripe | null = useStripe();
  const elements: StripeElements | null = useElements();
  const [cardStatus, setCardStatus] = useState({ isCardComplete: false, errorMessage: '' });
  const [pageState, setPageState] = useState<PageState>('MAKE_PAYMENT');
  const [isShowErrors, setIsShowErrors] = useState(false);
  const [isPayEmailConfirmationModalOpen, setIsPayEmailConfirmationModalOpen] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [paymentType, setPaymentType] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(false);
  const [verifiedFiservCardData, setVerifiedFiservCardData] = useState<FiservCardDetails | null>(
    null
  );

  // Plaid/Dwolla state
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [plaidPublicToken, setPlaidPublicToken] = useState('');
  const [selectedPlaidAccountDetails, setSelectedPlaidAccountDetails] = useState<{
    accountName: string;
    institutionName: string;
    bankAccountType: string;
    mask: string;
  } | null>(null);

  const { plaidTokenDetails } = useAppSelector((state) => state.auth);

  const [paymentRequest, setpaymentRequest] = useState<DillPaymentRequestResponse | null>(null);

  const { resolveKey: resolveIdempotencyKey, clearKey: clearIdempotencyKey } = useIdempotencyKey();

  /**
   * Mirrors what the backend fingerprints for this payment request. Single-use
   * artefacts — the Fiserv card token and its 3DS data, the Plaid token from a
   * fresh Link session — are excluded: they differ on every attempt, so
   * including them would make an honest retry look like a new payment.
   */
  const buildPaymentRequestSignature = (
    provider: 'STRIPE' | 'FISERV' | 'DWOLLA',
    extra: Record<string, unknown>
  ) => ({
    provider,
    paymentRequestId: paymentRequest?.id ?? null,
    amountPaid: paymentRequest?.balance ?? null,
    ...extra,
  });
  const [jobSuccessDetails, setJobSuccessDetails] = useState<{
    type: 'Error' | 'Success';
    details: string;
    title: string;
  }>({
    type: 'Success',
    details: '',
    title: '',
  });

  const getPaymentRequest = async ({ paymentRequestId }: { paymentRequestId: string }) => {
    const results = await dispatch(getPaymentRequestDetailsForPayment({ paymentRequestId }));
    if (results.type === 'paymentRequests/getPaymentRequestDetailsForPayment/fulfilled') {
      const payload: any = results.payload;
      const pr = payload.data.data as DillPaymentRequestResponse;
      setpaymentRequest(pr);
      getBasicSupplier({ supplierDomainId: pr.supplierId });
    } else {
      setPageState('PAYMENT_FETCH_ERROR');
      const errorMsg = results.payload as any;
      setFetchError(errorMsg ?? 'Error fetching payment request');
    }
  };

  useEffect(() => {
    const pathSplit = location.pathname.split('/');
    if (location.pathname && pathSplit.length === 3) {
      getPaymentRequest({ paymentRequestId: pathSplit[pathSplit.length - 1] });
    }
    return () => {};
  }, [location]);

  useEffect(() => {
    if (paymentRequest) {
      dispatch(getPlaidLinkTokenNoAuth({ id: paymentRequest.id, isIncludeStatement: false }));
    }
    return () => {};
  }, [paymentRequest]);

  useEffect(() => {
    if (plaidTokenDetails && plaidTokenDetails.link_token) {
      setLinkToken(plaidTokenDetails.link_token);
    }
    return () => {};
  }, [plaidTokenDetails]);

  const handleCardChange = (event: StripeCardElementChangeEvent) => {
    setCardStatus({
      isCardComplete: event.complete,
      errorMessage: !event.complete ? event.error?.message || '' : '',
    });
  };

  useEffect(() => {
    if (paymentRequest && paymentRequest.balance <= 0) {
      setPageState('PAYMENT_SUCCESS');
      setJobSuccessDetails({
        type: 'Success',
        details: 'Please check your email for the receipt.',
        title: 'Payment already made!',
      });
    }
  }, [paymentRequest]);

  const checkForErrors = () => {
    let isError = false;

    if (paymentType === 'bankTransfer') {
      if (!plaidPublicToken || !selectedPlaidAccountDetails) {
        isError = true;
      }
    } else {
      if (
        (paymentRequest?.supplier?.cardPaymentProvider === 'STRIPE' ||
          !paymentRequest?.supplier?.cardPaymentProvider) &&
        !cardStatus.isCardComplete
      ) {
        isError = true;
      }
      if (paymentRequest?.supplier?.cardPaymentProvider === 'FISERV' && !verifiedFiservCardData) {
        isError = true;
      }
    }
    return isError;
  };

  const { buyerFee } = useMemo(() => {
    if (
      paymentRequest &&
      paymentRequest?.supplier &&
      (paymentRequest?.supplier?.cardPaymentProvider === 'STRIPE' ||
        !paymentRequest?.supplier.cardPaymentProvider) &&
      paymentRequest.buyer &&
      paymentType === 'creditCard'
    ) {
      const supplier = paymentRequest?.supplier;
      const amount = paymentRequest.balance;
      const calculateAmountFee = calculateStripePartialFacilitationFeesAndAmount({
        amount,
        supplier,
        buyer: {
          ...paymentRequest.buyer,
          whoPaysCreditCardFee: paymentRequest.whoPaysFee,
          ...(paymentRequest.buyerTakeRate && paymentRequest.whoPaysFee === 'BOTH'
            ? {
                creditCardTakeRate: paymentRequest.buyerTakeRate,
                creditCardTakeRateExtra: undefined,
              }
            : { creditCardTakeRate: undefined, creditCardTakeRateExtra: undefined }),
        },
      });
      return { buyerFee: calculateAmountFee.buyerFees };
    }
    if (
      paymentRequest &&
      paymentRequest?.supplier &&
      paymentRequest?.supplier?.cardPaymentProvider === 'FISERV' &&
      paymentRequest.buyer &&
      paymentType === 'creditCard'
    ) {
      const amount = paymentRequest.balance;
      const calculateAmountFee = calculateFiservFee({
        amount,
        percentageRate: paymentRequest.buyerTakeRate || 0,
      });
      return { buyerFee: calculateAmountFee.buyerFees };
    }
    return { buyerFee: 0 };
  }, [paymentRequest, paymentType]);

  const creditCardTakeRate = useMemo(() => {
    if (
      paymentRequest &&
      paymentRequest?.supplier &&
      (paymentRequest?.supplier?.cardPaymentProvider === 'STRIPE' ||
        !paymentRequest?.supplier.cardPaymentProvider) &&
      paymentRequest.buyer
    ) {
      const supplier = paymentRequest?.supplier;
      const calculateAmountFee = calculateStripePartialFacilitationFeesAndAmount({
        amount: 100,
        supplier: { ...supplier, creditCardTakeRateExtra: 0 },
        buyer: {
          ...{ ...paymentRequest.buyer, creditCardTakeRateExtra: 0 },
          whoPaysCreditCardFee: paymentRequest.whoPaysFee,
          ...(paymentRequest.buyerTakeRate && paymentRequest.whoPaysFee === 'BOTH'
            ? {
                creditCardTakeRate: paymentRequest.buyerTakeRate,
                creditCardTakeRateExtra: undefined,
              }
            : { creditCardTakeRate: undefined, creditCardTakeRateExtra: undefined }),
        },
      });
      return calculateAmountFee.buyerFees;
    }
    if (
      paymentRequest &&
      paymentRequest?.supplier &&
      paymentRequest?.supplier.cardPaymentProvider === 'FISERV' &&
      paymentRequest.buyer
    ) {
      return paymentRequest.buyerTakeRate;
    }
    return 0;
  }, [paymentRequest]);

  const handlePlaidLinkSuccess: PlaidLinkOnSuccess = async (publicToken, metadata) => {
    setErrorMessage('');
    setSelectedPlaidAccountDetails({
      accountName: metadata.accounts[0].name,
      institutionName: metadata.institution?.name || '',
      mask: metadata.accounts[0].mask,
      bankAccountType: metadata.accounts[0]?.subtype || '',
    });
    // Exchange Plaid's Public Token for a Processor Token
    try {
      setIsLoading(true);
      const response = await axios.post(
        BASE_API_URL + `/generics/plaidGenerics/publicToken`,
        { accountId: metadata.accounts[0].id, publicToken },
        {}
      );
      setIsLoading(false);

      if (response.data && response.data.success) {
        setPlaidPublicToken(response.data?.data?.processorToken);
      }
    } catch (error: any) {
      setIsLoading(false);
      setSelectedPlaidAccountDetails(null);
      console.error('Error exchanging Plaid token:', error);
      setErrorMessage('Failed to verify bank account. Please try again.');
    }
  };

  const {
    open: openPlaidLink,
    ready: isPlaidLinkReady,
    error: plaidError,
  } = usePlaidLink({
    onSuccess: handlePlaidLinkSuccess,
    token: linkToken,
    onExit: (error, metadata) => {
      dispatch(getPlaidLinkToken({ isIncludeStatement: false }));
      if (error) {
        Sentry.captureException(error);
      }
      console.log('Plaid exited:', error, metadata);
    },
  });

  useEffect(() => {
    if (plaidError) {
      console.log({ plaidError });
      Sentry.captureException(plaidError);
      setErrorMessage(`Failed to load bank verification: ${plaidError.message}`);
    }

    return () => {};
  }, [plaidError]);

  const handlePlaid = () => {
    setErrorMessage('');
    openPlaidLink();
  };

  const handleStripePaymentRequestPayment = async ({
    confirmationEmail,
  }: {
    confirmationEmail: string;
  }) => {
    setIsShowErrors(true);
    if (!paymentType) {
      setErrorMessage('Please select a payment method type');
      return;
    }
    if (!stripe || !elements || !paymentRequest) {
      return;
    }
    if (checkForErrors()) {
      return;
    }
    if (!currentPaymentMethod) {
      return;
    }
    await appDelay(10);
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Processing payment',
        subTitle: 'This may take a few minutes. Please do not refresh the page',
      })
    );

    if (
      paymentType === 'creditCard' &&
      currentPaymentMethod.card?.funding &&
      currentPaymentMethod.card?.funding.toUpperCase() !== 'CREDIT'
    ) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Wrong Card',
          subTitle:
            'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.',
        })
      );
      return;
    }
    if (
      paymentType === 'debitOrOtherCard' &&
      currentPaymentMethod.card?.funding &&
      currentPaymentMethod.card?.funding.toUpperCase() === 'CREDIT'
    ) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Wrong Card',
          subTitle:
            'The card entered is a credit card. Please change your payment method selection to Credit Card.',
        })
      );

      return;
    }
    setErrorMessage('');
    const paymentResponse = await dispatch(
      paymentRequestPayment({
        amountPaid: paymentRequest.balance,
        paymentMethodId: currentPaymentMethod.id,
        paymentRequestId: paymentRequest.id,
        confirmationEmail,
        idempotencyKey: resolveIdempotencyKey(
          buildPaymentRequestSignature('STRIPE', {
            paymentMethodId: currentPaymentMethod.id,
            confirmationEmail,
          })
        ),
        paymentMethodName: `${capitalizeWordsInSentence(currentPaymentMethod.card?.brand)} ****${
          currentPaymentMethod?.card?.last4
        }`,
      })
    );

    if (paymentResponse.type === 'paymentRequests/paymentRequestPayment/fulfilled') {
      const dataResults: any = paymentResponse.payload;
      if (dataResults && dataResults?.data?.data?.clientSecret) {
        const clientSecret = dataResults?.data?.data?.clientSecret;
        try {
          const stripeConfirmRes = await stripe?.confirmCardPayment(clientSecret, {});
          if (stripeConfirmRes?.error) {
            dispatch(
              openMessageModal({
                buttonText: 'Close',
                modalType: 'ERROR',
                title: 'Error processing payment',
                subTitle: `${stripeConfirmRes?.error?.message || ''}`,
              })
            );
            return;
          }
        } catch (err) {
          dispatch(
            openMessageModal({
              buttonText: 'Close',
              modalType: 'ERROR',
              title: 'Error processing payment',
              subTitle: 'Error validating payment. Please try again',
            })
          );
          return;
        }
      }
      // Payment landed — retire the key so any later attempt starts fresh. After
      // the card-confirmation branch above on purpose: while that is unresolved
      // the key must stay, so retrying replays rather than charging again.
      clearIdempotencyKey();
      const isReplay = (paymentResponse.payload as any)?.isReplay === true;
      dispatch(closeMessageModal());

      getPaymentRequest({ paymentRequestId: paymentRequest.id });
      setPageState('PAYMENT_SUCCESS');
      setJobSuccessDetails({
        type: 'Success',
        details: isReplay ? IDEMPOTENT_REPLAY_SUBTITLE : 'Please check your email for the receipt.',
        title: isReplay ? IDEMPOTENT_REPLAY_TITLE : 'Payment successfully sent!',
      });
      setIsPayEmailConfirmationModalOpen(false);
    } else if (paymentResponse.type === 'paymentRequests/paymentRequestPayment/rejected') {
      let errorMsg = '';
      if (paymentResponse.type === 'paymentRequests/paymentRequestPayment/rejected') {
        errorMsg = paymentResponse.payload as any;
      }
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error processing payment',
          subTitle: `Contact customer support. ${errorMsg}`,
        })
      );
    }
  };
  const handleFiservPaymentRequestPayment = async ({
    confirmationEmail,
  }: {
    confirmationEmail: string;
  }) => {
    setIsShowErrors(true);
    if (!paymentType) {
      setErrorMessage('Please select a payment method type');
      return;
    }
    if (!verifiedFiservCardData) {
      return;
    }
    if (!paymentRequest) {
      return;
    }
    if (checkForErrors()) {
      return;
    }

    await appDelay(10);
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Processing payment',
        subTitle: 'This may take a few minutes. Please do not refresh the page',
      })
    );

    if (
      paymentType === 'creditCard' &&
      verifiedFiservCardData.cardType.toUpperCase() !== 'CREDIT'
    ) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Wrong Card',
          subTitle:
            'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.',
        })
      );
      return;
    }
    if (
      paymentType === 'debitOrOtherCard' &&
      verifiedFiservCardData.cardType.toUpperCase() === 'CREDIT'
    ) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Wrong Card',
          subTitle:
            'The card entered is a credit card. Please change your payment method selection to Credit Card.',
        })
      );

      return;
    }

    if (
      paymentRequest?.supplier?.cybersourceSettings?.is3DSEnabled &&
      paymentRequest.is3DSecure &&
      verifiedFiservCardData.brand === 'AMEX' &&
      !paymentRequest?.supplier?.cybersourceSettings?.isAmexDirect
    ) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Card Not Permitted',
          subTitle:
            'American Express is not permitted for this transaction. Please try a different card type.',
        })
      );
      return;
    }

    setErrorMessage('');

    const paymentResponse = await dispatch(
      paymentRequestFiservPayment({
        amountPaid: paymentRequest.balance,
        paymentRequestId: paymentRequest.id,
        confirmationEmail,
        fiservCardDetails: verifiedFiservCardData,
        idempotencyKey: resolveIdempotencyKey(
          buildPaymentRequestSignature('FISERV', {
            confirmationEmail,
            card: {
              brand: verifiedFiservCardData.brand,
              last4: verifiedFiservCardData.last4,
              expiry: verifiedFiservCardData.expiry,
              cardType: verifiedFiservCardData.cardType,
            },
          })
        ),
      })
    );

    if (paymentResponse.type === 'paymentRequests/paymentRequestFiservPayment/fulfilled') {
      // Payment landed — retire the key so any later attempt starts fresh. After
      // the card-confirmation branch above on purpose: while that is unresolved
      // the key must stay, so retrying replays rather than charging again.
      clearIdempotencyKey();
      const isReplay = (paymentResponse.payload as any)?.isReplay === true;
      dispatch(closeMessageModal());

      getPaymentRequest({ paymentRequestId: paymentRequest.id });
      setPageState('PAYMENT_SUCCESS');
      setJobSuccessDetails({
        type: 'Success',
        details: isReplay ? IDEMPOTENT_REPLAY_SUBTITLE : 'Please check your email for the receipt.',
        title: isReplay ? IDEMPOTENT_REPLAY_TITLE : 'Payment successfully sent!',
      });
      setIsPayEmailConfirmationModalOpen(false);
    } else if (paymentResponse.type === 'paymentRequests/paymentRequestFiservPayment/rejected') {
      let errorMsg = '';
      if (paymentResponse.type === 'paymentRequests/paymentRequestFiservPayment/rejected') {
        errorMsg = paymentResponse.payload as any;
      }
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error processing payment',
          subTitle: `Contact customer support. ${errorMsg}`,
        })
      );
    }
  };

  const handleDwollaPaymentRequestPayment = async ({
    confirmationEmail,
  }: {
    confirmationEmail: string;
  }) => {
    setIsShowErrors(true);
    if (!paymentType) {
      setErrorMessage('Please select a payment method type');
      return;
    }
    if (!plaidPublicToken || !selectedPlaidAccountDetails) {
      setErrorMessage('Please connect your bank account first');
      return;
    }
    if (!paymentRequest) {
      return;
    }
    if (checkForErrors()) {
      return;
    }

    await appDelay(10);
    dispatch(
      openMessageModal({
        buttonText: '',
        modalType: 'LOADING',
        title: 'Processing payment',
        subTitle: 'This may take a few minutes. Please do not refresh the page',
      })
    );

    setErrorMessage('');

    const paymentResponse = await dispatch(
      paymentRequestDwollaPayment({
        amountPaid: paymentRequest.balance,
        paymentRequestId: paymentRequest.id,
        confirmationEmail,
        plaidToken: plaidPublicToken,
        fundingSourceName: `${selectedPlaidAccountDetails.institutionName} ****${selectedPlaidAccountDetails.mask}`,
        bankAccountType: selectedPlaidAccountDetails.bankAccountType,
        idempotencyKey: resolveIdempotencyKey(
          buildPaymentRequestSignature('DWOLLA', {
            confirmationEmail,
            fundingSourceName: `${selectedPlaidAccountDetails.institutionName} ****${selectedPlaidAccountDetails.mask}`,
            bankAccountType: selectedPlaidAccountDetails.bankAccountType,
          })
        ),
      })
    );

    if (paymentResponse.type === 'paymentRequests/paymentRequestDwollaPayment/fulfilled') {
      // Payment landed — retire the key so any later attempt starts fresh. After
      // the card-confirmation branch above on purpose: while that is unresolved
      // the key must stay, so retrying replays rather than charging again.
      clearIdempotencyKey();
      const isReplay = (paymentResponse.payload as any)?.isReplay === true;
      dispatch(closeMessageModal());

      getPaymentRequest({ paymentRequestId: paymentRequest.id });
      setPageState('PAYMENT_SUCCESS');
      setJobSuccessDetails({
        type: 'Success',
        details: isReplay ? IDEMPOTENT_REPLAY_SUBTITLE : 'Please check your email for the receipt.',
        title: isReplay ? IDEMPOTENT_REPLAY_TITLE : 'Payment successfully sent!',
      });
      setIsPayEmailConfirmationModalOpen(false);
    } else if (paymentResponse.type === 'paymentRequests/paymentRequestDwollaPayment/rejected') {
      let errorMsg = '';
      if (paymentResponse.type === 'paymentRequests/paymentRequestDwollaPayment/rejected') {
        errorMsg = paymentResponse.payload as any;
      }
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error processing payment',
          subTitle: `Contact customer support. ${errorMsg}`,
        })
      );
    }
  };

  const paymentOptions = useMemo(() => {
    // The payment request is the only source of truth here — it already carries the customer's or
    // supplier's disabled types from when it was created.
    const disabledPaymentTypes = paymentRequest?.disabledPaymentTypes ?? [];

    const options: { id: string; name: string }[] = [];
    if (!disabledPaymentTypes.includes('CREDIT_CARD')) {
      options.push({ id: 'creditCard', name: 'Credit Card' });
    }
    if (!disabledPaymentTypes.includes('DEBIT_CARD')) {
      options.push({ id: 'debitOrOtherCard', name: 'Debit / Other Card' });
    }
    if (!disabledPaymentTypes.includes('BANK')) {
      options.push({ id: 'bankTransfer', name: 'Bank Transfer' });
    }

    return options;
  }, [paymentRequest]);

  const handleVerifyStripePaymentRequestPayment = async () => {
    setIsShowErrors(true);
    if (!paymentType) {
      setErrorMessage('Please select a payment method type');
      return;
    }
    setErrorMessage('');
    if (!stripe || !elements || !paymentRequest) {
      return;
    }
    if (checkForErrors()) {
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setErrorMessage('Failed to get card element');
      return;
    }
    setIsLoading(true);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    setIsLoading(false);
    if (error) {
      dispatch(
        openMessageModal({
          buttonText: 'Close',
          modalType: 'ERROR',
          title: 'Error validating card',
          subTitle: `${error}`,
        })
      );

      return;
    }
    setCurrentPaymentMethod(paymentMethod);

    if (
      paymentType === 'creditCard' &&
      paymentMethod.card?.funding &&
      paymentMethod.card?.funding.toUpperCase() !== 'CREDIT'
    ) {
      setErrorMessage(
        'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.'
      );
      return;
    }
    if (
      paymentType === 'debitOrOtherCard' &&
      paymentMethod.card?.funding &&
      paymentMethod.card?.funding.toUpperCase() === 'CREDIT'
    ) {
      setErrorMessage(
        'The card entered is a credit card. Please change your payment method selection to Credit Card.'
      );
      return;
    }
    setIsPayEmailConfirmationModalOpen(true);
  };
  const handleVerifyFiservPaymentRequestPayment = async () => {
    setIsShowErrors(true);
    if (!paymentType) {
      setErrorMessage('Please select a payment method type');
      return;
    }
    setErrorMessage('');
    if (paymentRequest?.supplier?.cardPaymentProvider === 'FISERV' && !verifiedFiservCardData) {
      return;
    }
    if (checkForErrors()) {
      return;
    }

    setIsLoading(true);

    setIsLoading(false);

    if (paymentType === 'creditCard' && verifiedFiservCardData?.cardType !== 'CREDIT') {
      setErrorMessage(
        'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.'
      );
      return;
    }
    if (paymentType === 'debitOrOtherCard' && verifiedFiservCardData?.cardType === 'CREDIT') {
      setErrorMessage(
        'The card entered is a credit card. Please change your payment method selection to Credit Card.'
      );
      return;
    }
    setIsPayEmailConfirmationModalOpen(true);
  };

  const handleVerifyDwollaPaymentRequestPayment = async () => {
    setIsShowErrors(true);
    if (!paymentType) {
      setErrorMessage('Please select a payment method type');
      return;
    }
    setErrorMessage('');
    if (!plaidPublicToken || !selectedPlaidAccountDetails) {
      setErrorMessage('Please connect your bank account first');
      return;
    }
    if (checkForErrors()) {
      return;
    }
    setIsPayEmailConfirmationModalOpen(true);
  };

  const handleVerifyPayment = async () => {
    if (paymentType === 'bankTransfer') {
      await handleVerifyDwollaPaymentRequestPayment();
    } else if (paymentRequest?.supplier?.cardPaymentProvider === 'FISERV') {
      await handleVerifyFiservPaymentRequestPayment();
    } else {
      await handleVerifyStripePaymentRequestPayment();
    }
  };

  const handleConfirmPayment = (confirmationEmail: string) => {
    setIsPayEmailConfirmationModalOpen(false);
    if (paymentType === 'bankTransfer') {
      handleDwollaPaymentRequestPayment({ confirmationEmail });
    } else if (paymentRequest?.supplier?.cardPaymentProvider === 'FISERV') {
      handleFiservPaymentRequestPayment({ confirmationEmail });
    } else {
      handleStripePaymentRequestPayment({ confirmationEmail });
    }
  };

  const handleFiservCardResult = (result: FiservCardResult) => {
    // console.log({ result });
    if (result.success) {
      setVerifiedFiservCardData(result.data);
    } else {
      setVerifiedFiservCardData(null);
    }
  };

  return (
    <div className="w-full GREY_100-BG min-h-full pb-4">
      <PayEmailConfirmationModal
        confirmationMassage={
          paymentRequest
            ? paymentType === 'bankTransfer'
              ? `The bank account ${selectedPlaidAccountDetails?.institutionName} ****${
                  selectedPlaidAccountDetails?.mask
                } will be charged ${formatMoney(
                  paymentRequest.balance,
                  2
                )}.\nPlease confirm the transaction below.`
              : `The submitted card details will be charged ${formatMoney(
                  paymentRequest.balance + (buyerFee || 0),
                  2
                )}.\nPlease confirm the transaction below.`
            : ''
        }
        open={isPayEmailConfirmationModalOpen}
        onSubmit={handleConfirmPayment}
        handleClose={() => {
          setIsPayEmailConfirmationModalOpen(false);
        }}
      />
      <div className="w-full flex items-center justify-center text-xl WHITE-BG font-bold p-5">
        Payment Request
      </div>
      <div className="flex flex-col px-4 md:px-6 py-4 w-full max-w-screen-md mx-auto">
        {paymentRequest && paymentRequest?.supplier && paymentRequest?.supplier.logo?.url && (
          <div className="flex flex-col items-center mb-4">
            <img className="h-20 sm:h-12 md:h-16 w-auto" src={paymentRequest?.supplier.logo.url} />
            <div className="flex items-center mt-2">
              <p>Powered By</p>
              <img className="h-8 md:h-6 sm:h-4 ml-2 w-auto" src={dillLogoWithText} />
            </div>
          </div>
        )}
        {(!paymentRequest?.supplier ||
          (paymentRequest?.supplier && !paymentRequest?.supplier.logo?.url)) && (
          <div className="flex flex-col items-center mb-4 w-full">
            <img className="h-20 sm:h-12 md:h-16 w-auto" src={dillLogoWithText} />
          </div>
        )}
        <div className="flex justify-center items-center">
          <div className="w-full">
            <div className="text-center font-semibold">
              Hi <span className="capitalize">{`${paymentRequest?.buyer?.name ?? ''}`}</span>,
              you've been requested by{' '}
              <span className="PRIMARY_500-CLR">{`${paymentRequest?.supplier?.name ?? ''} `}</span>
              to make the payment below.
            </div>

            {pageState === 'MAKE_PAYMENT' && (
              <div className="WHITE-BG w-full mt-4 rounded-lg px-1 pt-2">
                <div className="flex flex-col text-center">
                  <p className="mr-2 text-xs font-semibold">Payment Amount</p>
                  {paymentRequest && (
                    <p className="text-3xl md:text-2xl sm:text-xl font-bold">
                      {formatMoney(paymentRequest.balance, 2)}
                    </p>
                  )}
                </div>

                {paymentRequest?.notes && (
                  <div className="flex flex-col text-center mt-4">
                    <p className="mr-2 text-xs font-semibold">Notes</p>
                    {paymentRequest && <p className="text-sm">{paymentRequest.notes}</p>}
                  </div>
                )}

                {paymentRequest?.attachments && paymentRequest?.attachments.length > 0 && (
                  <div className="flex flex-col items-center text-center mt-4">
                    <p className="mr-2 text-xs font-semibold">Attachments</p>
                    <div className="flex items-center justify-center flex-wrap w-4/5">
                      {paymentRequest?.attachments &&
                        paymentRequest.attachments?.map((attachment, i) => {
                          return (
                            <div key={`${i}-o`} className="  my-1 mx-2 text-sm">
                              <a
                                className="flex items-center text-blue-400"
                                href={attachment.url}
                                target="_blank">
                                <span className="">
                                  {attachment.name && (
                                    <p>
                                      {attachment.name.length > 5
                                        ? `${attachment.name?.slice(0, 5)}...`
                                        : attachment.name}

                                      {attachment.name.length > 5
                                        ? `${attachment.name.slice(
                                            attachment.name.length - 6,
                                            attachment.name.length
                                          )}`
                                        : `${attachment.name.slice(
                                            attachment.name.length - 3,
                                            attachment.name.length
                                          )}`}
                                    </p>
                                  )}
                                </span>
                                <Download size={16} className="text-blue-500" />
                              </a>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {paymentRequest &&
                  paymentRequest?.balance !== 0 &&
                  paymentRequest?.status !== 'PAYMENT_INITIATED' && (
                    <div className="flex flex-col justify-between w-full pb-2  p-3 mt-3  bg-slate-50 rounded-md">
                      <div className="flex flex-row justify-between w-full pt-1 ">
                        <div className="text-base font-bold TEXT_SECONDARY-CLR">Amount</div>
                        <div className="text-black font-bold text-lg">{`${formatMoney(
                          paymentRequest.balance,
                          2
                        )}`}</div>
                      </div>
                      {buyerFee > 0 && paymentType === 'creditCard' && (
                        <div className="flex flex-row justify-between w-full pt-1">
                          <div className="text-base font-bold TEXT_SECONDARY-CLR">Fees</div>
                          <div className="text-black font-bold text-lg">{`${formatMoney(
                            buyerFee,
                            2
                          )}`}</div>
                        </div>
                      )}

                      <div className="flex flex-row justify-between w-full pt-1  border-t">
                        <div className="text-base font-bold">Total Payment</div>
                        <div className="PRIMARY_500-CLR font-bold text-lg">{`${formatMoney(
                          paymentRequest.balance + buyerFee,
                          2
                        )}`}</div>
                      </div>
                    </div>
                  )}

                <div className="flex flex-col mt-2">
                  <div className="flex flex-row items-center justify-between">
                    <p className="text-sm font-bold ml-4 mb-1">Select a Payment Method</p>
                  </div>

                  {paymentOptions.map((option, i) => {
                    return (
                      <div key={i + 'oo'} className="w-full px-4 my-1">
                        <button
                          className={
                            'flex flex-row w-full rounded-xl WHITE-BG px-5 py-4 border  items-center cursor-pointer ' +
                            (option.id === paymentType ? 'PRIMARY_500-BORDER' : ' GREY_300-BORDER')
                          }
                          onClick={() => {
                            setPaymentType(option.id);
                            // Reset bank transfer state when switching payment types
                            if (option.id !== 'bankTransfer') {
                              setPlaidPublicToken('');
                              setSelectedPlaidAccountDetails(null);
                            }
                          }}>
                          <div className="flex mr-2 items-center">
                            <AppRadioButton isChecked={option.id === paymentType} />
                            <div className="payment-type-logo">
                              {option.name === 'Bank Transfer' ? (
                                <Bank size={30} style={{ display: 'flex', alignSelf: 'center' }} />
                              ) : (
                                <CreditCard
                                  size={30}
                                  style={{ display: 'flex', alignSelf: 'center' }}
                                />
                              )}
                            </div>
                          </div>
                          <div className="flex justify-between w-full items-center">
                            <div className="flex flex-col">
                              <p className="text-sm font-bold text-left">{option.name}</p>
                            </div>
                            {option.id === 'creditCard' &&
                            creditCardTakeRate &&
                            creditCardTakeRate > 0 ? (
                              <div className="flex-end">
                                <div className="text-sm TEXT_SECONDARY-CLR ">Fees</div>
                                <div className="text-xs PRIMARY_500-CLR">{`${creditCardTakeRate}%`}</div>
                              </div>
                            ) : (
                              <></>
                            )}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Bank Transfer Section */}
                {paymentType === 'bankTransfer' && (
                  <div className="flex flex-col p-4 PRIMARY_50-BG mt-2">
                    <p className="mb-3 md:text-sm font-bold">Bank Details</p>
                    <div
                      className={twMerge(
                        'rounded-xl p-4 cursor-pointer border-2 transition-all duration-200',
                        'bg-white',
                        !isPlaidLinkReady
                          ? 'opacity-50 cursor-not-allowed border-gray-300'
                          : 'border-gray-300 hover:border-blue-400 hover:shadow-md'
                      )}
                      onClick={() => {
                        if (isPlaidLinkReady) {
                          handlePlaid();
                        }
                      }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 pr-2 md:pr-3">
                          <p className="text-sm md:text-sm font-semibold mb-1">
                            Connect your bank account
                          </p>
                          <p className="text-xs text-gray-500 mb-2">
                            Click to verify your bank instantly via Plaid secure link
                          </p>
                        </div>
                        <div className={twMerge('flex-shrink-0 transition-colors text-blue-600')}>
                          <Bank size={24} className="sm:size-10 md:size-12" />
                        </div>
                      </div>
                      <p className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Instant verification
                      </p>
                    </div>
                    {selectedPlaidAccountDetails && (
                      <div className="mt-4">
                        <p className="text-sm mb-1 font-bold">Selected bank details</p>
                        <p className="text-xs mb-1">
                          {`${selectedPlaidAccountDetails.accountName} from ${selectedPlaidAccountDetails.institutionName}`}
                        </p>
                      </div>
                    )}
                    {isShowErrors && !plaidPublicToken && (
                      <div className="flex flex-col mt-2">
                        <p className="text-[#F04438] text-2xs">
                          Please connect your bank account to continue.
                        </p>
                      </div>
                    )}
                    {isShowErrors && errorMessage && (
                      <p className="text-[#F04438] text-xs mt-2">{errorMessage}</p>
                    )}
                  </div>
                )}

                {/* Card Details Section - only show for card payments */}
                {paymentType !== 'bankTransfer' && paymentType !== '' && (
                  <div className="flex flex-col p-4 PRIMARY_50-BG mt-6">
                    <p className="mb-3 md:text-sm">Card Details</p>
                    {(paymentRequest?.supplier?.cardPaymentProvider === 'STRIPE' ||
                      !paymentRequest?.supplier?.cardPaymentProvider) && (
                      <div
                        className={twMerge(
                          'px-1 py-3  rounded-sm',
                          isShowErrors && !cardStatus.isCardComplete
                            ? 'border border-[#F04438]'
                            : ''
                        )}>
                        <label className="text-xs mb-1" htmlFor="card-element">
                          <CardElement
                            id="card-element"
                            onChange={handleCardChange}
                            options={{ disableLink: true }}
                          />
                        </label>
                        {isShowErrors && !cardStatus.isCardComplete && (
                          <div className="flex flex-col">
                            <p className="text-[#F04438] text-2xs mt-3">
                              Make sure to enter the right card details.
                            </p>
                            <p className="text-[#F04438] text-2xs">{cardStatus?.errorMessage}</p>
                          </div>
                        )}
                        {isShowErrors && errorMessage && (
                          <p className="text-[#F04438] text-xs mt-3">{errorMessage}</p>
                        )}
                      </div>
                    )}
                    {paymentRequest?.supplier?.cardPaymentProvider === 'FISERV' && (
                      <div
                        className={twMerge(
                          'px-1 py-3  rounded-sm bg-white',
                          isShowErrors && !verifiedFiservCardData ? 'border border-[#F04438]' : ''
                        )}>
                        {paymentRequest?.supplier?.cybersourceSettings?.is3DSEnabled &&
                        paymentRequest.is3DSecure ? (
                          <AppCustom3DSFiservCardInput
                            supplierId={paymentRequest?.supplier?.id}
                            onResult={handleFiservCardResult}
                            amount={`${paymentRequest.balance.toFixed(2)}`}
                            isAmexDirect={
                              paymentRequest?.supplier?.cybersourceSettings?.isAmexDirect
                            }
                          />
                        ) : (
                          <AppFiservCardInput onResult={handleFiservCardResult} />
                        )}
                        {isShowErrors && !verifiedFiservCardData && (
                          <div className="flex flex-col">
                            <p className="text-[#F04438] text-2xs mt-3">
                              Make sure to enter the right card details.
                            </p>
                          </div>
                        )}
                        {isShowErrors && errorMessage && (
                          <p className="text-[#F04438] text-xs mt-3">{errorMessage}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
                <div className="mb-2 GREY_500-CLR text-xs flex w-full justify-between">
                  <div className="mb-2 GREY_500-CLR text-xs">
                    By continuing, you agree to the{' '}
                    <a
                      href="https://www.usedill.com/terms"
                      target="_blank"
                      className="PRIMARY_500-CLR underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="https://www.usedill.com/privacy"
                      target="_blank"
                      className="PRIMARY_500-CLR underline">
                      Privacy Policy
                    </a>
                  </div>

                  {paymentType !== 'bankTransfer' && (
                    <img
                      src={CardBrands}
                      alt={'card brands'}
                      className="h-8 w-auto object-contain"
                    />
                  )}
                </div>

                <div className="flex justify-center px-4 py-2 w-full">
                  <AppButton
                    text="Pay"
                    isDisabled={
                      isLoading ||
                      paymentRequest?.balance === 0 ||
                      paymentRequest?.status === 'PAYMENT_INITIATED' ||
                      checkForErrors()
                    }
                    isLoading={isLoading}
                    onClick={handleVerifyPayment}
                    buttonStyles={{
                      marginTop: '4px',
                      marginBottom: '4px',
                      width: '100px',
                      height: '40px',
                    }}
                  />
                </div>
              </div>
            )}

            {pageState === 'PAYMENT_SUCCESS' && (
              <JobSavingSuccess
                type={jobSuccessDetails.type}
                details={jobSuccessDetails.details}
                title={jobSuccessDetails.title}
              />
            )}
            {pageState === 'PAYMENT_FETCH_ERROR' && (
              <JobSavingSuccess
                type={'Error'}
                details={'The payment may have been deleted by the supplier.'}
                title={'Payment request not found'}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentRequestPaymentPage;
